import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Database, Play, RefreshCcw, CheckCircle2, AlertTriangle, AlertCircle,
  HardDrive, Clock, Activity, FileArchive,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Header } from '../../components/layout/Header';

const HEALTH_BADGE = {
  ok:      { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Saudável" },
  stale:   { color: "bg-amber-50 text-amber-700 border-amber-200",       icon: AlertTriangle, label: "Atrasado" },
  missing: { color: "bg-rose-50 text-rose-700 border-rose-200",          icon: AlertCircle,   label: "Sem backups" },
};

export default function BackupsAdmin() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/events?limit=20"),
      ]);
      setData(s.data); setEvents(e.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar status");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const trigger = async () => {
    if (!window.confirm("Iniciar backup manual agora?")) return;
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/backups/trigger");
      toast.success(r.message || "Backup iniciado");
      setTimeout(load, 8000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao iniciar");
    } finally { setRunning(false); }
  };

  if (loading && !data) {
    return <div className="admin-text-muted py-12 text-center">Carregando…</div>;
  }

  const health = data?.health || "missing";
  const HBadge = HEALTH_BADGE[health];
  const HIcon = HBadge.icon;

  return (
    <div className="space-y-8" data-testid="backups-admin">
      <Header title="Backups" subtitle="Cópias de segurança do banco de dados" actions={<><button onClick={load} className="admin-btn-secondary inline-flex items-center gap-2" data-testid="backups-refresh"><RefreshCcw size={14} /> Atualizar</button><button onClick={trigger} disabled={running} className="admin-btn-primary inline-flex items-center gap-2" data-testid="backups-trigger"><Play size={14} /> {running ? "Iniciando…" : "Backup agora"}</button></>} />

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="admin-glass-card p-5">
          <div className="flex items-center justify-between">
            <p className="eyebrow admin-text-muted">Status</p>
            <Activity size={16} className="admin-text-gold" />
          </div>
          <div className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${HBadge.color}`} data-testid="backups-health">
            <HIcon size={12} /> {HBadge.label}
          </div>
          {data?.hours_since_last !== null && data?.hours_since_last !== undefined && (
            <p className="text-[11px] admin-text-muted mt-2">
              Último backup há <strong>{data.hours_since_last}h</strong>
            </p>
          )}
        </div>

        <div className="admin-glass-card p-5">
          <div className="flex items-center justify-between">
            <p className="eyebrow admin-text-muted">Quantidade</p>
            <FileArchive size={16} className="admin-text-gold" />
          </div>
          <p className="font-serif text-3xl mt-2" data-testid="backups-count">{data?.count ?? 0}</p>
          <p className="text-[11px] admin-text-muted mt-1">arquivos retidos</p>
        </div>

        <div className="admin-glass-card p-5">
          <div className="flex items-center justify-between">
            <p className="eyebrow admin-text-muted">Espaço usado</p>
            <HardDrive size={16} className="admin-text-gold" />
          </div>
          <p className="font-serif text-3xl mt-2">{data?.total_size_human || "0"}</p>
          <p className="text-[11px] admin-text-muted mt-1 truncate">{data?.directory}</p>
        </div>

        <div className="admin-glass-card p-5">
          <div className="flex items-center justify-between">
            <p className="eyebrow admin-text-muted">Próxima execução</p>
            <Clock size={16} className="admin-text-gold" />
          </div>
          <p className="font-serif text-xl mt-2">03:00</p>
          <p className="text-[11px] admin-text-muted mt-1">cron diário · retenção 30 dias</p>
        </div>
      </div>

      {/* Alert de falha */}
      {data?.last_failure && (
        <div className="border border-rose-300 bg-rose-50 text-rose-900 p-4 rounded-lg flex gap-3" data-testid="backups-last-failure">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Última falha detectada</p>
            <p className="text-xs mt-1 font-mono">{data.last_failure}</p>
            <p className="text-xs mt-2 admin-text-muted">
              Tente disparar manualmente acima. Se persistir, verifique o log no servidor.
            </p>
          </div>
        </div>
      )}

      {/* Listagem de arquivos */}
      <div className="admin-glass-card p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
          <Database size={16} className="admin-text-gold" />
          <h3 className="font-serif text-lg">Arquivos disponíveis</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider admin-text-muted bg-zinc-50">
              <tr>
                <th className="text-left px-4 py-2">Arquivo</th>
                <th className="text-left px-4 py-2">Criado em</th>
                <th className="text-right px-4 py-2">Tamanho</th>
              </tr>
            </thead>
            <tbody>
              {(data?.files || []).length === 0 && (
                <tr><td colSpan={3} className="text-center py-10 admin-text-muted">
                  Nenhum backup encontrado. Clique em "Backup agora" pra criar o primeiro.
                </td></tr>
              )}
              {(data?.files || []).map((f) => (
                <tr key={f.name} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`backup-file-${f.name}`}>
                  <td className="px-4 py-2 font-mono text-xs">{f.name}</td>
                  <td className="px-4 py-2 text-xs admin-text-muted">{new Date(f.created_at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-2 text-right font-medium">{f.size_human}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 text-[11px] admin-text-muted">
          Restauração é feita via terminal no VPS por questões de segurança:
          <code className="ml-1 px-1.5 py-0.5 bg-white rounded border border-zinc-200">bash /opt/clinicadzorzi/scripts/mongo-restore.sh &lt;arquivo&gt;</code>
        </div>
      </div>

      {/* Eventos recentes */}
      <div className="admin-glass-card p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100">
          <h3 className="font-serif text-lg">Eventos recentes</h3>
        </div>
        {events.length === 0 ? (
          <p className="px-5 py-10 text-center admin-text-muted text-sm">Sem eventos registrados.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {events.map((e) => (
              <li key={e.id} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                <span className={`w-2 h-2 rounded-full ${e.status === "ok" ? "bg-emerald-500" : "bg-rose-500"}`} />
                <span className="admin-text-muted text-xs w-44 shrink-0">{new Date(e.created_at).toLocaleString("pt-BR")}</span>
                <span className="capitalize text-xs font-medium w-12 shrink-0">{e.status}</span>
                <span className="text-xs truncate">{e.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Log tail */}
      {data?.log_tail && (
        <details className="admin-glass-card p-0 overflow-hidden">
          <summary className="px-5 py-3 cursor-pointer text-sm font-medium">Log técnico (últimas 40 linhas)</summary>
          <pre className="bg-zinc-900 text-zinc-100 text-[11px] font-mono p-4 overflow-x-auto max-h-72">{data.log_tail}</pre>
        </details>
      )}
    </div>
  );
}
